/**
 * Platform-specific cart fetchers ported from `ometrics-ai` (v1).
 * Normalizes carts into the same common shape v1 used:
 *   { token, total_price, items_subtotal_price, currency, items: [{ product_id, ... }] }
 */
 
export async function fetchShopifyCart() {
  try {
    const response = await fetch('/cart.js')
    if (!response.ok) return null
    const cart = await response.json()
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return null
 
    const normalized = {
      token: cart.token,
      total_price: cart.total_price,
      item_count: cart.item_count,
      original_total_price: cart.original_total_price,
      total_discount: cart.total_discount,
      total_weight: cart.total_weight,
      requires_shipping: cart.requires_shipping,
      currency: cart.currency,
      items_subtotal_price: cart.items_subtotal_price,
      items: [],
    }
 
    cart.items.forEach((item) => {
      normalized.items.push({
        id: item.id,
        quantity: item.quantity,
        variant_id: item.variant_id,
        key: item.key,
        title: encodeURIComponent(item.title),
        price: item.price,
        original_price: item.original_price,
        discounted_price: item.discounted_price,
        line_price: item.line_price,
        original_line_price: item.original_line_price,
        total_discount: item.total_discount,
        sku: item.sku,
        grams: item.grams,
        vendor: encodeURIComponent(item.vendor),
        taxable: item.taxable,
        product_id: item.product_id,
        final_price: item.final_price,
        final_line_price: item.final_line_price,
        url: item.url,
        image: item.image,
        handle: item.handle,
        requires_shipping: item.requires_shipping,
      })
    })
 
    return normalized
  } catch {
    return null
  }
}
 
export async function fetchBigCommerceCart() {
  try {
    const url = `https://${window.location.hostname}/api/storefront/carts`
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) return null
    const carts = await response.json()
    if (!Array.isArray(carts) || carts.length === 0) return null
    const cart = carts[0]
    if (!cart || !cart.lineItems) return null
 
    const itemCount =
      (cart.lineItems.customItems?.length || 0) +
      (cart.lineItems.digitalItems?.length || 0) +
      (cart.lineItems.giftCertificates?.length || 0) +
      (cart.lineItems.physicalItems?.length || 0)
    if (!itemCount) return null
 
    const normalized = {
      token: cart.id,
      total_price: cart.cartAmount,
      item_count: itemCount,
      original_total_price: cart.cartAmount,
      total_discount: cart.discountAmount,
      total_weight: 0,
      requires_shipping: '',
      currency: cart.currency,
      items_subtotal_price: cart.cartAmount,
      items: [],
    }
 
    const lineItemTypes = [
      'customItems',
      'digitalItems',
      'giftCertificates',
      'physicalItems',
    ]
    lineItemTypes.forEach((type) => {
      const rows = cart.lineItems?.[type]
      if (!Array.isArray(rows)) return
      rows.forEach((item) => {
        normalized.items.push({
          id: item.productId,
          quantity: item.quantity,
          variant_id: item.variantId,
          key: item.id,
          title: encodeURIComponent(item.name),
          price: item.salePrice,
          original_price: item.listPrice,
          discounted_price: item.salePrice,
          line_price: item.extendedListPrice,
          original_line_price: item.extendedSalePrice,
          total_discount: item.discountAmount,
          sku: item.sku,
          grams: 0,
          vendor: encodeURIComponent(item.brand || ''),
          taxable: item.isTaxable,
          product_id: item.productId,
          final_price: item.extendedSalePrice,
          final_line_price: item.salePrice,
          url: item.url,
          image: item.imageUrl,
          handle: item.id,
          requires_shipping: item.isShippingRequired,
        })
      })
    })
 
    return normalized
  } catch {
    return null
  }
}
 
export async function fetchMagentoCart() {
  try {
    const mageStorage = localStorage.getItem('mage-cache-storage')
    if (!mageStorage) return null
    const mage = JSON.parse(mageStorage)
    const cart = mage?.cart
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return null
 
    const normalized = {
      token: '',
      total_price: cart.subtotalAmount,
      item_count: cart.summary_count,
      original_total_price: cart.subtotalAmount,
      total_discount: 0,
      total_weight: 0,
      requires_shipping: '',
      currency: '',
      items_subtotal_price: cart.subtotalAmount,
      items: [],
    }
 
    cart.items.forEach((item) => {
      normalized.items.push({
        id: item.product_id,
        quantity: item.qty,
        variant_id: item.item_id,
        key: '',
        title: encodeURIComponent(item.product_name),
        price: item.product_price_value,
        original_price: item.product_price_value,
        discounted_price: item.product_price_value,
        line_price: item.product_price_value,
        original_line_price: item.product_price_value,
        total_discount: 0,
        sku: item.product_sku,
        grams: 0,
        vendor: '',
        taxable: 0,
        product_id: item.product_id,
        final_price: item.product_price_value,
        final_line_price: item.product_price_value,
        url: item.product_url,
        image: item.product_image?.src || '',
        handle: '',
        requires_shipping: 0,
      })
    })
 
    return normalized
  } catch {
    return null
  }
}
 
export async function fetchWooCommerceCart(agentId) {
  try {
    if (!agentId) return null
    const cartStr = localStorage.getItem(`ometricsCart${agentId}`)
    const cartDateStr = localStorage.getItem(`ometricsCartDate${agentId}`)
    if (!cartStr || !cartDateStr) return null
    const cart = JSON.parse(cartStr)
    if (!cart || typeof cart !== 'object') return null
    if (!Array.isArray(cart.items) || cart.items.length === 0) return null
    return cart
  } catch {
    return null
  }
}
 
export async function getCartFromPlatform(shopType, agentId) {
  if (!shopType) return null
  const normalized = String(shopType).toLowerCase()
  switch (normalized) {
    case 'shopify':
      return fetchShopifyCart()
    case 'bigcommerce':
      return fetchBigCommerceCart()
    case 'magento':
      return fetchMagentoCart()
    case 'woocommerce':
      return fetchWooCommerceCart(agentId)
    default:
      return null
  }
}

